/* eslint-disable sonarjs/cognitive-complexity */
/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { IconButton, Typography, Modal, Box, Tooltip } from "@mui/material";
import "../../OEM-Admin-Components/DetailsPopUp/DetailsPopup.css";
import Grid from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GlobalTaxTablePopUp from "../GlobalTaxTablePopUp/GlobalTaxTablePopUp";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: "600",
    border: 0,
    paddingTop: 8,
    paddingBottom: 8,
    borderBottom: "1px solid #d0d0d0 !important",
    fontSize: 12,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "td, th": {
    border: 0,
    paddingTop: 5,
    paddingBottom: 5,
    borderBottom: "1px solid #e8e8e8",
  },
}));

function GlobalDetailsPopup({ onClose, row, listType }) {
  const [plans, setPlans] = useState([]);
  const [productDetails, setProductDetails] = useState({});
  const [showTaxDetailsModal, setShowTaxDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [oneTimePrices, setOneTimePrices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const frequencyUnitConverter = {
    "1-DAY": "Daily",
    "1-MONTH": "Monthly", 
    "3-MONTH": "Quarterly",
    "6-MONTH": "Half yearly",
    "1-YEAR": "Yearly",
  };

  const fetchProductDetails = useCallback(async () => {
    if (!row) return;
    
    try {
      if (row.type === "recurring") {
        // Check if this is a Premium recurring product (from Premium table)
        if (listType === "Premium") {
          // Use getPlanDetails API for Premium recurring products
          const details = await APIManager.getPlanDetails(row.productId, row.planId);
          setProductDetails(details);
          
          // Transform the API response for Premium recurring products
          if (details.recurringPlans && details.recurringPlans.length > 0) {
            const plansData = details.recurringPlans.map((plan) => ({
              planId: plan.id,
              subscriptionFrequency: plan.recurringFrequency,
              subscriptionFrequencyUnit: plan.recurringFrequencyUnit,
              price: plan.basePrice?.price || 0,
              currencyCode: plan.basePrice?.currencyCode || "USD",
              productId: details.product?.id || row.productId,
              name: plan.name
            }));
            setPlans(plansData);
          }
        } else {
          // Use getRecurringAppDetails for regular InApp recurring products
          const details = await APIManager.getRecurringAppDetails(row.id);
          setProductDetails(details);
          
          if (details.recurringPlans && details.recurringPlans.length > 0) {
            // Transform the API response to match the table structure
            const plansData = details.recurringPlans.map((plan) => ({
              planId: plan.id,
              subscriptionFrequency: plan.recurringFrequency,
              subscriptionFrequencyUnit: plan.recurringFrequencyUnit,
              price: plan.basePrice?.price || 0,
              currencyCode: plan.basePrice?.currencyCode || "USD",
              productId: details.product?.id || row.id,
              name: plan.name
            }));
            setPlans(plansData);
          }
        }
      } else if (row.type === "oneTime") {
        // Use getOneTimeAppDetails for one-time products
        const details = await APIManager.getOneTimeAppDetails(row.id);
        setProductDetails(details);
        
        // Process country-specific pricing from the existing data
        if (details.oneTimePlan?.planPrices?.length > 0) {
          let priceRows = [];
          details.oneTimePlan.planPrices.forEach((price) => {
            priceRows.push({
              id: price.id,
              country: price.country,
              countryCode: price.countryCode,
              currencyCode: price.currencyCode,
              price: Number(price.price || 0).toFixed(2),
              taxRate: Number(price.taxRate || 0).toFixed(2),
              taxRateinPercent: price.taxRateinPercent,
            });
          });
          setOneTimePrices(priceRows);
        }
      }
    } catch (error) {
      // Fallback to empty state if API fails
      console.error("Error fetching product details:", error);
      setPlans([]);
      setProductDetails({});
    }
  }, [row, listType]);

  useEffect(() => {
    if (row?.id) {
      fetchProductDetails();
    }
  }, [row, fetchProductDetails]);

  const openTaxDetailsModal = () => {
    setShowTaxDetailsModal(true);
  };

  const handleCloseTaxDetailsPopup = () => {
    setShowTaxDetailsModal(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  let trialPeriodText = "7 Days";
  if (row?.trialPeriod) {
    trialPeriodText = `${row.trialPeriod} Days`;
  }
  if (productDetails.trialPrice?.trialPeriod) {
    trialPeriodText = `${productDetails.trialPrice.trialPeriod} Days`;
  }

  const visibleRows = React.useMemo(
    () =>
      [...oneTimePrices].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, oneTimePrices]
  );

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 750,
    bgcolor: "background.paper",
    borderRadius: "4px",
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };

  return (
    <div style={{ maxHeight: "80vh", overflow: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          In-App Products Pricing
        </Typography>
        <IconButton>
          <CloseIcon onClick={onClose} sx={{ padding: "none" }} />
        </IconButton>
      </div>
      <div style={{marginTop: "3%"}}>

        {row?.type === "recurring" && (
          <div style={{ maxWidth: "100%", overflow: "hidden" }}>
            {listType === "Premium" ? (
              // Premium recurring product details
              <>
                <Grid container spacing={1} sx={{ maxWidth: "100%" }}>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Tooltip title={row.name || productDetails.product?.name || "Plan Name"} arrow>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom 
                        sx={{ 
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                          cursor: "help"
                        }}
                      >
                        <span className="product-details-modal-info-label">
                          Plan Name:
                        </span>
                        <span className="product-details-modal-info-value">
                          {row.name || productDetails.product?.name || "Plan Name"}
                        </span>
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      sx={{ 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%"
                      }}
                    >
                      <span className="product-details-modal-info-label">
                        Recurring Price:
                      </span>
                      <span className="product-details-modal-info-value">
                        {Number(row.price || 0).toFixed(2)} {row.currencyCode || "USD"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      sx={{ 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%"
                      }}
                    >
                      <span className="product-details-modal-info-label">
                        Recurring Frequency:
                      </span>
                      <span className="product-details-modal-info-value">
                        {productDetails.recurringPlans && productDetails.recurringPlans.length > 0
                          ? frequencyUnitConverter[
                              productDetails.recurringPlans[0].recurringFrequency +
                                "-" +
                                productDetails.recurringPlans[0].recurringFrequencyUnit
                            ] || "Monthly"
                          : "Monthly"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      sx={{ 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%"
                      }}
                    >
                      <span className="product-details-modal-info-label">
                        When to Charge:
                      </span>
                      <span className="product-details-modal-info-value">
                        Immediately
                      </span>
                    </Typography>
                  </Grid>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      sx={{ 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%"
                      }}
                    >
                      <span className="product-details-modal-info-label">
                        Grace Period:
                      </span>
                      <span className="product-details-modal-info-value">
                        {productDetails.recurringPlans && productDetails.recurringPlans.length > 0 && productDetails.recurringPlans[0].gracePeriod
                          ? `${productDetails.recurringPlans[0].gracePeriod} Days`
                          : "3 Days"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      sx={{ 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%"
                      }}
                    >
                      <span className="product-details-modal-info-label">
                        Auto Renew:
                      </span>
                      <span className="product-details-modal-info-value">
                        Enabled
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
                
                <div style={{ marginTop: "20px" }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={"bold"}>
                    Country specific price settings
                  </Typography>
                  {productDetails.recurringPlans && 
                   productDetails.recurringPlans.length > 0 && 
                   productDetails.recurringPlans[0].planPrices && 
                   productDetails.recurringPlans[0].planPrices.length > 0 ? (
                    <Paper sx={{ width: "100%", mb: 2 }} elevation={0}>
                      <TableContainer sx={{ maxWidth: "100%", overflow: "auto" }}>
                        <Table aria-label="customized table" sx={{ tableLayout: "fixed", width: "100%" }}>
                          <TableHead>
                            <TableRow>
                              <StyledTableCell sx={{ width: "40%" }}>Country</StyledTableCell>
                              <StyledTableCell sx={{ width: "30%" }}>Price</StyledTableCell>
                              <StyledTableCell sx={{ width: "30%" }}>Tax Rate</StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {productDetails.recurringPlans[0].planPrices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((price) => (
                              <StyledTableRow key={price.id}>
                                <StyledTableCell sx={{ fontWeight: "bold", width: "40%" }}>
                                  {price.country}
                                </StyledTableCell>
                                <StyledTableCell sx={{ width: "30%" }}>
                                  <span style={{ color: "#717273", marginRight: "10%" }}>
                                    {price.currencyCode}
                                  </span>
                                  {Number(price.price || 0).toFixed(2)}
                                </StyledTableCell>
                                <StyledTableCell sx={{ width: "30%" }}>
                                  {price.taxRateinPercent}% ({price.currencyCode} {Number(price.taxRate || 0).toFixed(2)})
                                </StyledTableCell>
                              </StyledTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={productDetails.recurringPlans[0].planPrices.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Items per page"
                      />
                    </Paper>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      No country-specific pricing available
                    </Typography>
                  )}
                </div>
              </>
            ) : (
              // InApp recurring product details (existing layout)
              <>
                <Grid container spacing={1} sx={{ maxWidth: "100%" }}>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Tooltip title={row.productId} arrow>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom 
                        sx={{ 
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                          cursor: "help"
                        }}
                      >
                        <span className="product-details-modal-info-label">
                          Product Id:
                        </span>
                        <span className="product-details-modal-info-value">
                          {row.productId}
                        </span>
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Tooltip title={productDetails.product?.name || "Plan Name goes here"} arrow>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom 
                        sx={{ 
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                          cursor: "help"
                        }}
                      >
                        <span className="product-details-modal-info-label">
                          Plan Name:
                        </span>
                        <span className="product-details-modal-info-value">
                          {productDetails.product?.name || "Plan Name goes here"}
                        </span>
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      sx={{ 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%"
                      }}
                    >
                      <span className="product-details-modal-info-label">
                        Trial Period:
                      </span>
                      <span className="product-details-modal-info-value">
                        {trialPeriodText}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid size={4} sx={{ minWidth: 0 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom 
                      sx={{ 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%"
                      }}
                    >
                      <span className="product-details-modal-info-label">
                        Recurrence:
                      </span>
                      <span className="product-details-modal-info-value">
                        Enabled
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
                
                <div>
                  <Typography variant="subtitle2" gutterBottom>
                    Plans
                  </Typography>
                  {plans.length > 0 && (
                    <TableContainer sx={{ maxWidth: "100%", overflow: "auto" }}>
                      <Table aria-label="customized table" sx={{ tableLayout: "fixed", width: "100%" }}>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell sx={{ width: "30%" }}>
                              Subscription Frequency
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: "20%" }}>Price</StyledTableCell>
                            <StyledTableCell sx={{ width: "45%" }}>Offers</StyledTableCell>
                            <StyledTableCell sx={{ width: "5%" }}></StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {plans.map((plan) => (
                            <StyledTableRow key={plan.planId}>
                              <StyledTableCell
                                sx={{ 
                                  fontWeight: "bold", 
                                  width: "30%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}
                              >
                                {frequencyUnitConverter[
                                  plan.subscriptionFrequency +
                                    "-" +
                                    plan.subscriptionFrequencyUnit
                                ]}
                              </StyledTableCell>
                              <StyledTableCell sx={{ 
                                width: "20%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}>
                                <span
                                  style={{ color: "#717273", marginRight: "10%" }}
                                >
                                  USD
                                </span>
                                {plan.price ? plan.price.toFixed(2) : "10.00"}
                              </StyledTableCell>
                              <StyledTableCell sx={{ 
                                width: "45%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}>
                                ---
                              </StyledTableCell>
                              <StyledTableCell>
                                <ArrowForwardIcon 
                                  className="edit-icon" 
                                  onClick={() => {
                                    setSelectedProduct(plan);
                                    openTaxDetailsModal();
                                  }}
                                />
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {row?.type === "oneTime" && (
          <div style={{ maxWidth: "100%", overflow: "hidden" }}>
            <Grid container spacing={1} sx={{ maxWidth: "100%" }}>
              <Grid size={4} sx={{ minWidth: 0 }}>
                <Tooltip title={productDetails.product?.name || "Plan Name"} arrow>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom 
                    sx={{ 
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                      cursor: "help"
                    }}
                  >
                    <span className="product-details-modal-info-label">
                      Plan Name:
                    </span>
                    <span className="product-details-modal-info-value">
                      {productDetails.product?.name || "Plan Name"}
                    </span>
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid size={4} sx={{ minWidth: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom 
                  sx={{ 
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%"
                  }}
                >
                  <span className="product-details-modal-info-label">
                    Base Price:
                  </span>
                  <span className="product-details-modal-info-value">
                    {productDetails.oneTimePlan?.basePrice?.currencyCode || "USD"} {Number(productDetails.oneTimePlan?.basePrice?.price || row.price || 0).toFixed(2)}
                  </span>
                </Typography>
              </Grid>
              <Grid size={4} sx={{ minWidth: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom 
                  sx={{ 
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%"
                  }}
                >
                  <span className="product-details-modal-info-label">
                    Preference:
                  </span>
                  <span className="product-details-modal-info-value">
                    One Time Purchase
                  </span>
                </Typography>
              </Grid>
            </Grid>
            
            <div>
              <Typography variant="subtitle2" gutterBottom fontWeight={"bold"}>
                Country specific price settings
              </Typography>
              {oneTimePrices.length > 0 ? (
                <Paper sx={{ width: "100%", mb: 2 }} elevation={0}>
                  <TableContainer sx={{ maxWidth: "100%", overflow: "auto" }}>
                    <Table aria-label="customized table" sx={{ tableLayout: "fixed", width: "100%" }}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell sx={{ width: "40%" }}>Country</StyledTableCell>
                          <StyledTableCell sx={{ width: "30%" }}>Price</StyledTableCell>
                          <StyledTableCell sx={{ width: "30%" }}>Tax Rate</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleRows.map((price) => (
                          <StyledTableRow key={price.id}>
                            <StyledTableCell sx={{ fontWeight: "bold", width: "40%" }}>
                              {price.country}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: "30%" }}>
                              <span style={{ color: "#717273", marginRight: "10%" }}>
                                {price.currencyCode}
                              </span>
                              {price.price}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: "30%" }}>
                              {price.taxRateinPercent}% ({price.currencyCode} {price.taxRate})
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={oneTimePrices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Items per page"
                  />
                </Paper>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No country-specific pricing available
                </Typography>
              )}
            </div>
          </div>
        )}
        <Modal
          open={showTaxDetailsModal}
          aria-labelledby="parent-modal-title"
          aria-describedby="parent-modal-description"
        >
          <Box sx={{ ...style, width: 750 }}>
            <div style={{ maxHeight: "70vh", overflow: "auto" }}>
              <GlobalTaxTablePopUp
                onClose={handleCloseTaxDetailsPopup}
                row={selectedProduct}
                listType={listType}
              />
            </div>
          </Box>
        </Modal>
      </div>
    </div>
  );
}

export default GlobalDetailsPopup;

GlobalDetailsPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  listType: PropTypes.string,
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currencyCode: PropTypes.string,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    planId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trialPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};